import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';

/**
 * Modello di scope della libreria attività, centralizzato in un solo posto per
 * evitare che LibraryPage, SaveToLibraryModal e LibraryActivitiesTable ricalcolino
 * (in modo potenzialmente divergente) chi appartiene a un'agenzia e con quali
 * permessi legge/scrive la libreria condivisa.
 *
 * Modello dati di riferimento (tabella `activity_library`):
 * - `owner_id`  = creatore della riga;
 * - `agency_id` = se valorizzato, la riga è nella libreria di quell'agenzia.
 *
 * "La mia agenzia":
 * - per l'account `agency` è il proprio `user.id` (personale ≡ agenzia);
 * - per il `travel_advisor_member` è `userData.agency_id`.
 */
export interface LibraryScopeModel {
  /** Id della "mia agenzia" (vedi sopra), o null se l'utente non ne ha una. */
  agencyId: string | null;
  /** L'utente è l'account agenzia stesso (`userRole === 'agency'`). */
  isAgencyAccount: boolean;
  /** L'utente è un membro collegato a un'agenzia (ha `agency_id`, ma non è l'account agency). */
  isAgencyMember: boolean;
  /** Può leggere la libreria dell'agenzia (account agenzia o membro). */
  canReadAgency: boolean;
  /** Salva sempre direttamente in libreria agenzia (solo l'account agency). */
  canWriteAgencyDirect: boolean;
  /** Può promuovere le proprie attività personali in libreria agenzia (solo il membro). */
  canPromote: boolean;
}

export const useLibraryScope = (): LibraryScopeModel => {
  const { user, userData, userRole } = useAuth();

  return useMemo(() => {
    const isAgencyAccount = userRole === 'agency';
    const isAgencyMember = !!userData?.agency_id && userRole !== 'agency';
    const agencyId = isAgencyAccount
      ? user?.id ?? null
      : isAgencyMember
        ? userData?.agency_id ?? null
        : null;

    return {
      agencyId,
      isAgencyAccount,
      isAgencyMember,
      canReadAgency: isAgencyAccount || isAgencyMember,
      canWriteAgencyDirect: isAgencyAccount,
      canPromote: isAgencyMember,
    };
  }, [user?.id, userData?.agency_id, userRole]);
};
